import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatText } from '../../common/TextFormatterProvider';
import { getPeriodType, getPeriodColumnLabel, getPeriodConfig } from '../../../utils/periodUtils';
import { isExternalTable } from '../../../utils/tableTypes';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  '&:last-child': {
    borderRight: 'none',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  fontWeight: 500,
}));

const isGroup = (value) => value && typeof value === 'object' && value.type === undefined;

// The metric schema as [key, value] pairs in the table's persisted display
// order (`display_order.metric`: flattened leaf paths, "Group.Child" for a
// nested metric), so the read-only schema keeps the order the table was
// created with after a reload (L76). A group sits where its first child sits
// and its children follow their own order; anything the order does not name
// keeps its stored position after the named entries.
export const orderMetricSchema = (schema, metricOrder) => {
  const entries = Object.entries(schema || {});
  if (!Array.isArray(metricOrder) || metricOrder.length === 0) return entries;
  const rank = new Map(metricOrder.map((path, index) => [path, index]));
  const rankOf = (key, value) => {
    if (!isGroup(value)) return rank.get(key);
    const ranks = Object.keys(value)
      .map((child) => rank.get(`${key}.${child}`))
      .filter((childRank) => childRank !== undefined);
    return ranks.length ? Math.min(...ranks) : undefined;
  };
  const sortByRank = (pairs, rankFor) => pairs
    .map((pair, index) => ({ pair, index, rank: rankFor(pair) }))
    .sort((a, b) => {
      if (a.rank === undefined && b.rank === undefined) return a.index - b.index;
      if (a.rank === undefined) return 1;
      if (b.rank === undefined) return -1;
      return a.rank - b.rank;
    })
    .map(({ pair }) => pair);
  return sortByRank(entries, ([key, value]) => rankOf(key, value)).map(([key, value]) => (
    isGroup(value)
      ? [key, Object.fromEntries(sortByRank(Object.entries(value), ([child]) => rank.get(`${key}.${child}`)))]
      : [key, value]
  ));
};

// The scopes a detail column (a raw table's data column) or a metric leaf is
// mandatory in (Spec 6 section 2.2), as the creation form's Mandatory Columns
// step names them; '' when it is in neither.
export const mandatoryScopeLabel = (definition) => {
  if (definition.mandatory_per_file && definition.mandatory_per_unit) return 'mandatory per file and per unit';
  if (definition.mandatory_per_file) return 'mandatory per file';
  if (definition.mandatory_per_unit) return 'mandatory per unit';
  return '';
};

// A metric heading: name, type, update behaviour, and the scopes it is
// mandatory in.
const metricLabel = (key, value) => {
  const scope = mandatoryScopeLabel(value);
  return `${formatText(key)} (${value.type}, ${value.updateBehavior}${scope ? `, ${scope}` : ''})`;
};

const SCOPED_MANDATORY_COLUMNS = [
  ['Mandatory Per File', (column) => column.mandatory_per_file === true],
  ['Mandatory Per Unit', (column) => column.mandatory_per_unit === true],
];

function TableSchemaViewer({ schema, metricOrder = null }) {
  const isRaw = schema?.tableType === 'raw';
  const isTimeSeries = schema?.tableType === 'timeSeries';

  const isNestedValue = isGroup;

  // Function to render value column structure
  const renderMetricColumns = () => {
    if (!schema.metricColumns) return null;

    const valueSchema = orderMetricSchema(schema.metricColumns.schema, metricOrder);

    let totalColumns = 0;

    // Calculate total columns needed
    valueSchema.forEach(([key, value]) => {
      if (isNestedValue(value)) {
        totalColumns += Object.keys(value).length;
      } else {
        totalColumns += 1;
      }
    });

    // Get the period-specific column label (e.g., "Example Month", "Example Quarter", etc.)
    const periodType = getPeriodType(schema);
    const periodColumnLabel = getPeriodColumnLabel(periodType);

    // Create header rows
    const headers = [
      [{ text: periodColumnLabel, span: totalColumns }], // Period row (e.g., "Example Month", "Example Quarter")
      [], // Second row for first level keys
      []  // Third row for second level keys or type info
    ];

    // Process schema entries
    valueSchema.forEach(([key, value]) => {
      if (isNestedValue(value)) {
        // Nested structure
        const nestedSpan = Object.keys(value).length;
        headers[1].push({ text: formatText(key), span: nestedSpan });

        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          headers[2].push({ 
            text: metricLabel(nestedKey, nestedValue), 
            span: 1 
          });
        });
      } else {
        // Single layer
        headers[1].push({ 
          text: metricLabel(key, value), 
          span: 1,
          rowSpan: 2 
        });
      }
    });

    return (
      <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
        <Table size="small">
          <TableHead>
            {headers.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <StyledTableCell 
                    key={cellIndex}
                    align="center"
                    colSpan={cell.span}
                    rowSpan={cell.rowSpan}
                    sx={{ backgroundColor: theme => theme.palette.custom.tableHeader }}
                    >
                    {cell.text}
                  </StyledTableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
        </Table>
      </TableContainer>
    );
  };

  // Function to render a list of columns. `mandatoryColumns` is one
  // [heading, (column) => required] pair per requirement the list shows: the
  // identifier's and an attribute's (M224), a detail column's two scopes, none
  // for the table's constants or an external table's columns.
  const renderDetailColumns = (columns, title, mandatoryColumns = []) => {
    if (!columns || columns.length === 0) return null;

    return (
      <>
        <SectionTitle variant="h6">{title}</SectionTitle>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ backgroundColor: theme => theme.palette.custom.tableHeader }}>Column Name</StyledTableCell>
                <StyledTableCell sx={{ backgroundColor: theme => theme.palette.custom.tableHeader }}>Type</StyledTableCell>
                {mandatoryColumns.map(([heading]) => (
                  <StyledTableCell key={heading} sx={{ backgroundColor: theme => theme.palette.custom.tableHeader }}>{heading}</StyledTableCell>
                ))}
                <StyledTableCell sx={{ backgroundColor: theme => theme.palette.custom.tableHeader }}>Description</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {columns.map((column, index) => (
                <TableRow key={index}>
                  <StyledTableCell>{formatText(column.name)}</StyledTableCell>
                  <StyledTableCell>{column.type}</StyledTableCell>
                  {mandatoryColumns.map(([heading, required]) => (
                    <StyledTableCell key={heading}>{required(column) ? 'Yes' : 'No'}</StyledTableCell>
                  ))}
                  {/* Business text, shown as entered (L6). */}
                  <StyledTableCell>{column.description || '-'}</StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Box>
      {/* Identifier — the row key, present in every upload by definition */}
      {schema.identifier?.name && renderDetailColumns([schema.identifier], 'Identifier', [['Mandatory', () => true]])}

      {/* Attribute Columns — the upload-tag requirement */}
      {renderDetailColumns(schema.attributeColumns, 'Attribute Columns', [['Mandatory', (column) => Boolean(column.mandatory)]])}

      {/* Table Attribute Columns — constants stamped on every row */}
      {renderDetailColumns(schema.tableAttributeColumns, 'Table Attribute Columns')}

      {/* Details Columns — mandatory per file and per unit; an external table takes no uploads */}
      {renderDetailColumns(
        schema.detailColumns,
        isRaw ? 'Columns' : 'Details Columns',
        isExternalTable(schema) ? [] : SCOPED_MANDATORY_COLUMNS,
      )}

      {/* The reporting grain, stated whether or not metrics exist (M224) */}
      {isTimeSeries && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          Reporting period: <strong>{getPeriodConfig(getPeriodType(schema)).label}</strong>
        </Typography>
      )}

      {/* Metrics Columns */}
      {schema.metricColumns && (
        <>
          <SectionTitle variant="h6">Metrics Columns</SectionTitle>
          {renderMetricColumns()}
        </>
      )}
    </Box>
  );
}

export default TableSchemaViewer;
